import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { setUser } from '../store/userSlice'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "At least one number", test: (p) => /[0-9]/.test(p) },
  { label: "At least one special character", test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
]

const isPasswordValid = (pass) => PASSWORD_RULES.every((rule) => rule.test(pass))

const Profile = () => {
  const dispatch = useDispatch()
  const currentUser = useSelector((state) => state.user)
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    password: ''
  })
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordRulesVisible, setPasswordRulesVisible] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const passwordInputRef = useRef(null)
  const rulesRef = useRef(null)

  useEffect(() => {
    fetchUserProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const fetchUserProfile = async () => {
    const userId = localStorage.getItem('id')
    // if (!userId) {
    //   setErrorMessage("User session expired. Please login again.")
    //   setIsFetching(false)
    //   return
    // }

    setIsFetching(true)
    try {
      const response = await axios.post(API_URL, {
        action: "get-user",
        id: userId
      })

      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      if (responseData.success === true && responseData.user) {
        const user = responseData.user
        setFormData({
          name: user.name || '',
          phoneNumber: user.phoneNumber || '',
          password: ''
        })
        setEmail(user.email || '')
      } else {
        setErrorMessage(responseData.message || "Failed to fetch profile")
      }
    } catch (error) {
      console.error("Fetch profile error:", error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        setErrorMessage(errorData.message || "An error occurred while fetching profile")
      } else {
        setErrorMessage("Network error. Please check your connection and try again.")
      }
    } finally {
      setIsFetching(false)
    }
  }

  // Handle phone number input - only digits, max 10 digits
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '') // Remove all non-digits
    if (value.length <= 10) {
      setFormData({ ...formData, phoneNumber: value })
    }
  }

  // Password validation rules
  const passwordRules = PASSWORD_RULES

  // Handle click outside to hide password rules (only if password is valid)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        rulesRef.current &&
        !rulesRef.current.contains(event.target) &&
        passwordInputRef.current &&
        !passwordInputRef.current.contains(event.target)
      ) {
        // Only hide if password validation is met
        if (isPasswordValid(formData.password)) {
          // Smooth slide up animation for password rules
          const $ = window.$
          if ($) {
            $('.password-rules-wrapper').slideUp(300, () => {
              setPasswordRulesVisible(false)
            })
          } else {
            setPasswordRulesVisible(false)
          }
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [formData.password])

  // Handle other input fields
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage('')
    }
    if (passwordError && name === 'password') {
      setPasswordError('')
    }
  }

  // Form validation
  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage("Name is required")
      return false
    }
    if (!formData.phoneNumber.trim()) {
      setErrorMessage("Phone number is required")
      return false
    }
    if (formData.phoneNumber.length !== 10) {
      setErrorMessage("Phone number must be 10 digits")
      return false
    }
    
    // Password validation only if password change is shown
    if (showPasswordChange) {
      if (!formData.password || !confirmPassword) {
        setPasswordError("Please fill out both password fields.")
        return false
      }
      if (formData.password !== confirmPassword) {
        setPasswordError("Passwords do not match.")
        return false
      }
      if (!isPasswordValid(formData.password)) {
        setPasswordError("Password does not meet the required criteria.")
        return false
      }
    }
    
    return true
  }

  const formatRole = (role) => {
  if (!role) return '-'

  return role
    .toLowerCase()
    .replace(/[_\s]+/g, ' ') 
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Clear any previous error messages
    setErrorMessage('')
    setPasswordError('')

    // Validate form - this will prevent API call if validation fails
    if (!validateForm()) {
      return
    }

    // Additional check: If password change section is open, ensure password is valid
    if (showPasswordChange) {
      if (!isPasswordValid(formData.password)) {
        setPasswordError("Password does not meet the required criteria. Please check all validation rules.")
        return
      }
    }

    // Get currentUserId from localStorage
    const currentUserId = localStorage.getItem('id')
    // if (!currentUserId) {
    //   setErrorMessage("User session expired. Please login again.")
    //   return
    // }

    setIsLoading(true)

    try {
      const payload = {
        action: "update-profile",
        currentUserId: currentUserId,
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber.trim()
      }

      // Only include password if user wants to change it and validation passed
      if (showPasswordChange && formData.password && formData.password.trim()) {
        payload.password = formData.password.trim()
      }

      const response = await axios.post(API_URL, payload)

      // Parse the response - handle both string and object responses
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      // Check if update was successful
      if (responseData.success === true && responseData.user) {
        const user = responseData.user
        
        // Update Redux state with new user data
        dispatch(setUser({
          id: user.id,
          name: user.name || '',
          role: user.role || currentUser.role || 'user'
        }))

        toast.success(responseData.message || "Profile updated successfully", { autoClose: 2000 })
        
        // Reset password fields and hide password change section
        setFormData({ ...formData, password: '' })
        setConfirmPassword('')
        setPasswordError('')
        setPasswordRulesVisible(false)
        setShowPasswordChange(false)
        setShowPassword(false)
        setShowConfirmPassword(false)
      } else {
        setErrorMessage(responseData.message || "Failed to update profile")
      }
    } catch (error) {
      console.error("Update profile error:", error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        setErrorMessage(errorData.message || "An error occurred while updating profile")
      } else {
        setErrorMessage("Network error. Please check your connection and try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isFetching) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Profile</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Profile</h1>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      {errorMessage && (
                        <div className="alert alert-danger mb-3" role="alert">
                          {errorMessage}
                        </div>
                      )}
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Name</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="name"
                            className="form-control"
                            placeholder="Name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Email</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{email || '-'}</div>
                        </div>
                      </div>
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Phone Number</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="tel"
                            name="phoneNumber"
                            className="form-control"
                            placeholder="Phone Number"
                            value={formData.phoneNumber}
                            onChange={handlePhoneChange}
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                e.preventDefault()
                              }
                            }}
                            maxLength={10}
                            inputMode="numeric"
                            required
                          />
                          {/* <small className="form-text text-muted mt-1">Enter 10-digit phone number</small> */}
                        </div>
                      </div>
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Role</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply"> {formatRole(currentUser?.role)}</div>
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Password</label>
                        <div className="col-12 col-md-8">
                          <div className="mt-2 mb-3">
                            <a
                              href="#change-password"
                              className="f_cream change_pwdclk crsr_pntr"
                              onClick={(e) => {
                                e.preventDefault()
                                // jQuery toggle will handle the animation
                                const $ = window.$
                                if ($) {
                                  $('.change_pwddiv').toggle(1000)
                                }
                                // Update state for form validation
                                setShowPasswordChange(!showPasswordChange)
                                if (showPasswordChange) {
                                  // Smoothly hide password rules if visible
                                  if ($ && passwordRulesVisible) {
                                    $('.password-rules-wrapper').slideUp(300)
                                  }
                                  setFormData({ ...formData, password: '' })
                                  setConfirmPassword('')
                                  setPasswordError('')
                                  setPasswordRulesVisible(false)
                                  setShowPassword(false)
                                  setShowConfirmPassword(false)
                                }
                              }}
                            >
                              {showPasswordChange ? 'Cancel Password Change' : 'Change Password'}
                            </a>
                          </div>
                          <div className="change_pwddiv" style={{ display: 'none' }}>
                            <div className="innerbox">
                              <div className="mb-2 w-100">
                                <div className="input-group mb-3">
                                  <input
                                    ref={passwordInputRef}
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    className="form-control"
                                    placeholder="New Password"
                                    autoComplete="new-password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    onFocus={() => {
                                      setPasswordRulesVisible(true)
                                      // Smooth slide down animation for password rules
                                      const $ = window.$
                                      if ($) {
                                        setTimeout(() => {
                                          $('.password-rules-wrapper').slideDown(300)
                                        }, 10)
                                      }
                                    }}
                                    onBlur={(e) => {
                                      // When tabbing away, check if password is valid before hiding
                                      // Use setTimeout to allow focus to move to next field first
                                      setTimeout(() => {
                                        // Check if focus moved to confirm password or outside
                                        const activeElement = document.activeElement
                                        const isConfirmPasswordFocused = activeElement?.name === 'confirmPassword'
                                        if (isConfirmPasswordFocused) return

                                        // Only hide if password is valid
                                        if (isPasswordValid(formData.password)) {
                                          const $ = window.$
                                          if ($) {
                                            $('.password-rules-wrapper').slideUp(300, () => {
                                              setPasswordRulesVisible(false)
                                            })
                                          } else {
                                            setPasswordRulesVisible(false)
                                          }
                                        }
                                      }, 100)
                                    }}
                                  />
                                  <div className="input-group-append">
                                    <div 
                                      className="input-group-text crsr_pntr" 
                                      onClick={() => setShowPassword(!showPassword)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                                    </div>
                                  </div>
                                </div>
                                <div
                                  ref={rulesRef}
                                  className="password-rules-wrapper"
                                  style={{ 
                                    display: 'none',
                                    marginBottom: '10px'
                                  }}
                                >
                                  <ul className="mb-2" style={{ listStyle: 'none', paddingLeft: 0 }}>
                                    {passwordRules.map((rule, idx) => (
                                      <li key={idx} style={{ color: rule.test(formData.password) ? 'green' : 'gray' }}>
                                        <i
                                          className={`fa ${rule.test(formData.password) ? 'fa-check-circle' : 'fa-circle'}`}
                                          style={{ marginRight: 6 }}
                                        />
                                        {rule.label}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              <div className="w-100">
                                <div className="input-group mb-3">
                                  <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    className="form-control"
                                    placeholder="Confirm New Password"
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                      setConfirmPassword(e.target.value)
                                      if (passwordError) {
                                        setPasswordError('')
                                      }
                                    }}
                                    onFocus={() => {
                                      // When clicking on confirm password, hide rules if password is valid
                                      if (isPasswordValid(formData.password)) {
                                        const $ = window.$
                                        if ($) {
                                          $('.password-rules-wrapper').slideUp(300, () => {
                                            setPasswordRulesVisible(false)
                                          })
                                        } else {
                                          setPasswordRulesVisible(false)
                                        }
                                      }
                                    }}
                                  />
                                  <div className="input-group-append">
                                    <div 
                                      className="input-group-text crsr_pntr" 
                                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {passwordError && (
                                <div className="text-danger mb-2" style={{ fontSize: '0.9em' }}>
                                  {passwordError}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? <PulseLoader color="#FFF" size={8} /> : "Update Profile"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile